import {
  Heading,
  Table,
  Tr,
  Th,
  Td,
  Link,
  Flex,
  Text,
  Tbody,
  Badge,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  useToast
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import PreviewModal from '../../components/previewModal'
import { EntregaDeTrabalhoService } from '../../services/EntregaDeTrabalho/EntregaDeTrabalhoService'
import { courseFormat, downloadLink, previewLink } from '../Dashboard/EntregaTrabalho/helper'
import {
  EEntregaDeTrabalhoType,
  IEntregaDeTrabalhoComplete,
  IEntregaDeTrabalhoUpload,
  statusColors
} from '../Dashboard/EntregaTrabalho/models'

export function PreviewEntregaDeTrabalho() {
  const { id } = useParams<string>()
  const service = new EntregaDeTrabalhoService()
  const toast = useToast()
  const [entregaDeTrabalho, setEntregaDeTrabalho] = useState<IEntregaDeTrabalhoComplete>()
  const [previewModal, setPreviewModal] = useState(false)
  const [previewData, setPreviewData] = useState<{ url: string; fileName: string; fileSize: number }>({
    url: '',
    fileName: '',
    fileSize: 0
  })
  const [dots, setDots] = useState('...')
  const [form, setForm] = useState({
    professorName: '',
    startDate: '',
    endDate: '',
    workload: '',
    contractNumber: '',
    classCode: '',
    grade: '',
    observations: ''
  })

  const loadEntregaDeTrabalho = async () => {
    if (!id) return

    const loadingInterval = setInterval(() => {
      setDots((prevDots) => {
        if (prevDots.length < 3) {
          return prevDots + '.'
        }
        return '.'
      })
    }, 500)

    service
      .getByPublicId(id)
      .then((response) => {
        setEntregaDeTrabalho(response)

        if (response.review) {
          setForm({
            professorName: response.review.teacher,
            startDate: response.review.start_date,
            endDate: response.review.end_date,
            workload: response.review.course_load,
            contractNumber: response.review.contract_number,
            classCode: response.review.class_code,
            grade: response.review.grade,
            observations: response.review.observations
          })
        } else {
          setForm({ ...form, professorName: response.teacher })
        }
        clearInterval(loadingInterval)
      })
      .catch(handleLoadError)
  }

  useEffect(() => {
    loadEntregaDeTrabalho()
  }, [id])

  const handleLoadError = (error: any) => {
    if (error.response.status === 404) {
      alert('Entrega de trabalho não encontrada')
    } else {
      alert('Erro ao carregar entrega de trabalho')
    }
  }

  const handleOpenPreview = (upload: IEntregaDeTrabalhoUpload) => {
    setPreviewData({
      fileName: upload.original_name,
      url: previewLink(upload),
      fileSize: Number(upload.size)
    })

    setPreviewModal(true)
  }

  const handleGrade = (e: React.FocusEvent<HTMLInputElement>) => {
    let value = parseFloat(e.target.value)
    if (value < 0) {
      value = 0
    } else if (value > 10) {
      value = 10
    }

    const newValue = Math.round(value * 20) / 20

    setForm({ ...form, grade: newValue.toString() })
  }

  const handleSubmit = async () => {
    const allFieldsAreFilled = Object.values(form).every((value) => value.trim() !== '')
    const startDate = new Date(form.startDate)
    const endDate = new Date(form.endDate)

    if (!entregaDeTrabalho) {
      return
    }

    if (!allFieldsAreFilled) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Preencha todos os campos para submeter a revisão.',
        status: 'warning',
        duration: 5000,
        isClosable: true
      })
      return
    }

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      toast({
        title: 'Datas inválidas',
        description: 'A data de início e término devem ser datas válidas.',
        status: 'warning',
        duration: 5000,
        isClosable: true
      })
      return
    }

    if (startDate.getTime() > endDate.getTime()) {
      toast({
        title: 'Datas inválidas',
        description: 'A data de início deve ser anterior à data de término.',
        status: 'warning',
        duration: 5000,
        isClosable: true
      })
      return
    }

    if (isNaN(Number(form.workload))) {
      toast({
        title: 'Carga horária inválida',
        description: 'A carga horária deve ser um número válido.',
        status: 'warning',
        duration: 5000,
        isClosable: true
      })
      return
    }

    if (isNaN(Number(form.grade))) {
      toast({
        title: 'Nota inválida',
        description: 'A nota deve ser um número válido.',
        status: 'warning',
        duration: 5000,
        isClosable: true
      })
      return
    }

    await service.review(entregaDeTrabalho.id, {
      teacher: form.professorName,
      course_load: form.workload,
      start_date: form.startDate,
      end_date: form.endDate,
      contract_number: form.contractNumber,
      class_code: form.classCode,
      observations: form.observations,
      grade: form.grade
    })

    toast({
      title: 'Trabalho revisado',
      description: 'A revisão do trabalho foi submetida',
      status: 'success',
      duration: 5000,
      isClosable: true
    })
    loadEntregaDeTrabalho()
  }

  return !entregaDeTrabalho ? (
    <Flex className="bg-dark" h="100vh" align="center" justify="center" direction="column">
      <img src="/img/logo.png" className="mx-auto" />
      <Text color="white">Carregando{dots}</Text>
    </Flex>
  ) : (
    <>
      <div
        style={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}
        className="w-100 d-md-flex justify-content-md-center align-items-md-center p-0"
      >
        <div className="container p-0">
          <div className="row pb-0 mb-0">
            <div className="col-12 col-md-6 bg-white mx-auto p-0 shadow">
              <div className="bg-dark pt-4">
                <img src="/img/logo.png" className="mx-auto" />
              </div>

              <Heading size="lg" mt={4} textAlign="center">
                {EEntregaDeTrabalhoType[entregaDeTrabalho.type]}
              </Heading>
              <Table>
                <Tbody>
                  <Tr>
                    <Th>Status da avaliação</Th>
                    <Td>
                      <Badge colorScheme={statusColors[entregaDeTrabalho.status]}>{entregaDeTrabalho.status}</Badge>
                    </Td>
                  </Tr>
                  <Tr>
                    <Th>Nome</Th>
                    <Td>{entregaDeTrabalho.name}</Td>
                  </Tr>
                  <Tr>
                    <Th>CPF:</Th>
                    <Td>{entregaDeTrabalho.cpf}</Td>
                  </Tr>
                  <Tr>
                    <Th>Nome do Responsável:</Th>{' '}
                    <Td>
                      {entregaDeTrabalho.responsable_name == null || entregaDeTrabalho.responsable_name == ''
                        ? 'Não possui'
                        : entregaDeTrabalho.responsable_name}
                    </Td>
                  </Tr>
                  <Tr>
                    <Th>CPF do Responsável:</Th>{' '}
                    <Td>
                      {entregaDeTrabalho.responsable_cpf == null || entregaDeTrabalho.responsable_cpf == ''
                        ? 'Não possui'
                        : entregaDeTrabalho.responsable_cpf}
                    </Td>
                  </Tr>
                  <Tr>
                    <Th>E-mail:</Th>
                    <Td>{entregaDeTrabalho.email}</Td>
                  </Tr>
                  <Tr>
                    <Th>Telefone:</Th>
                    <Td>{entregaDeTrabalho.phone}</Td>
                  </Tr>
                  <Tr>
                    <Th>Curso:</Th>
                    <Td>{entregaDeTrabalho.course}</Td>
                  </Tr>
                  <Tr>
                    <Th>Modalidade:</Th>
                    <Td>{courseFormat[entregaDeTrabalho.format as 'presencial' | 'online' | 'online-ao-vivo']}</Td>
                  </Tr>
                  <Tr>
                    <Th>Professor:</Th>
                    <Td>{entregaDeTrabalho.teacher}</Td>
                  </Tr>
                  <Tr>
                    <Th>Dias de Aula:</Th>
                    <Td>{entregaDeTrabalho['class-days']}</Td>
                  </Tr>
                  <Tr>
                    <Th>Horário das Aulas:</Th>
                    <Td>
                      {entregaDeTrabalho['class-hour-start'].substring(0, 5)} às{' '}
                      {entregaDeTrabalho['class-hour-ends'].substring(0, 5)}
                    </Td>
                  </Tr>
                  <Tr>
                    <Th>Tipo de Trabalho:</Th>
                    <Td>{EEntregaDeTrabalhoType[entregaDeTrabalho.type]}</Td>
                  </Tr>
                  <Tr>
                    <Th>Data da submissão:</Th>
                    <Td>{new Date(entregaDeTrabalho.created_at).toLocaleString('pt-BR')}</Td>
                  </Tr>
                </Tbody>
              </Table>
              <Table>
                <Tbody>
                  <Tr>
                    <Th colSpan={3} textAlign="center">
                      Arquivos
                    </Th>
                  </Tr>
                  {entregaDeTrabalho.uploads.map((upload) => (
                    <Tr key={upload.id}>
                      <Th>
                        {upload.original_name}{' '}
                        <small className="text-muted">({(Number(upload.size) / 1024 / 1024).toFixed(2)} Mb)</small>
                      </Th>
                      <Td>
                        <Link color={'blue.500'} onClick={() => handleOpenPreview(upload)}>
                          <i className="lni lni-eye"></i>
                        </Link>
                      </Td>
                      <Td>
                        <Link target="_blank" href={downloadLink(upload)} rel="noreferrer" color={'blue.500'}>
                          <i className="lni lni-download"></i>
                        </Link>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              <Table>
                <Tbody>
                  <Tr>
                    <Th textAlign="center">Avaliação/Revisão</Th>
                  </Tr>
                  <Tr>
                    <Td>
                      <div className="row">
                        <div className="col-12">
                          <FormControl>
                            <FormLabel fontSize="sm">Nome do professor</FormLabel>
                            <Input
                              size="sm"
                              readOnly={!!entregaDeTrabalho.review}
                              value={form.professorName}
                              onChange={(e) => setForm({ ...form, professorName: e.target.value })}
                            />
                          </FormControl>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-12 col-md-6 mt-2">
                          <FormControl>
                            <FormLabel fontSize="sm">Data de início</FormLabel>
                            <Input
                              size="sm"
                              readOnly={!!entregaDeTrabalho.review}
                              type="date"
                              value={form.startDate}
                              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                              max={form.endDate}
                            />
                          </FormControl>
                        </div>
                        <div className="col-12 col-md-6 mt-2">
                          <FormControl>
                            <FormLabel fontSize="sm">Data de término</FormLabel>
                            <Input
                              size="sm"
                              readOnly={!!entregaDeTrabalho.review}
                              type="date"
                              value={form.endDate}
                              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                              min={form.startDate}
                            />
                          </FormControl>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-12 col-md-6 mt-2">
                          <FormControl>
                            <FormLabel fontSize="sm">Carga horária (horas)</FormLabel>
                            <Input
                              size="sm"
                              readOnly={!!entregaDeTrabalho.review}
                              type="number"
                              value={form.workload}
                              onChange={(e) => setForm({ ...form, workload: e.target.value })}
                            />
                          </FormControl>
                        </div>
                        <div className="col-12 col-md-6 mt-2">
                          <FormControl>
                            <FormLabel fontSize="sm">Nº do contrato</FormLabel>
                            <Input
                              size="sm"
                              readOnly={!!entregaDeTrabalho.review}
                              value={form.contractNumber}
                              onChange={(e) => setForm({ ...form, contractNumber: e.target.value })}
                            />
                          </FormControl>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-12 col-md-6 mt-2">
                          <FormControl>
                            <FormLabel fontSize="sm">Código da turma</FormLabel>
                            <Input
                              size="sm"
                              readOnly={!!entregaDeTrabalho.review}
                              value={form.classCode}
                              onChange={(e) => setForm({ ...form, classCode: e.target.value })}
                            />
                          </FormControl>
                        </div>
                        <div className="col-12 col-md-6 mt-2">
                          <FormControl>
                            <FormLabel fontSize="sm">Nota</FormLabel>
                            <Input
                              size="sm"
                              readOnly={!!entregaDeTrabalho.review}
                              type="number"
                              step="0.05"
                              value={form.grade}
                              onChange={(e) => setForm({ ...form, grade: e.target.value })}
                              onBlur={handleGrade}
                            />
                          </FormControl>
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-12">
                          <FormControl>
                            <FormLabel fontSize="sm">Observações</FormLabel>
                            <Textarea
                              size="sm"
                              readOnly={!!entregaDeTrabalho.review}
                              value={form.observations}
                              onChange={(e) => setForm({ ...form, observations: e.target.value })}
                            />
                          </FormControl>
                        </div>
                      </div>
                      {!entregaDeTrabalho.review && (
                        <div className="row mt-2">
                          <div className="col-12 text-end">
                            <Button colorScheme="magenta" type="button" size="sm" onClick={handleSubmit}>
                              Validar Ficha
                            </Button>
                          </div>
                        </div>
                      )}
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      <PreviewModal
        isOpen={previewModal}
        onClose={() => {
          setPreviewModal(false)
        }}
        url={previewData.url}
        fileName={previewData.fileName}
        fileSize={previewData.fileSize}
      />
    </>
  )
}
